import React, { useState, useMemo } from 'react';

import PropTypes from 'prop-types';
import { FaExclamationTriangle } from 'react-icons/fa';
import Overflow from 'react-overflow-indicator';
import styled, { css } from 'styled-components';

import Text from '~/shared/components/Text';
import { getPrices } from '~/shared/utils/embroidery';
import { Button, mobile } from '~/techstyle-shared/react-components';
import {
  FormattedMessage,
  defineMessages,
} from '~/techstyle-shared/react-intl';

import { useProductDetailEmbroideryContext } from '../ProductDetailCTAs/ProductDetailEmbroideryContext';

import { DynamicText, EmbroideryDetailSection } from './components';

const confirmationMessages = defineMessages({
  lookGood: {
    id: 'site_pdp.embroidery_everything_look_good',
    defaultMessage: 'Everything Look Good?',
  },
  skipped: {
    id: 'site_pdp.embroidery_skip_customization_PLACEHOLDER',
    defaultMessage: 'Skip Customization?',
  },
  confirm: {
    id: 'site_pdp.confirm',
    defaultMessage: 'Add Design',
  },
  cancel: {
    id: 'site_pdp.embroidery_cancel_and_close',
    defaultMessage: 'Cancel and Close',
  },
  cancelSkip: {
    id: 'site_pdp.confirm_no_skip',
    defaultMessage: 'No, Add Custom Embroidery',
  },
  confirmSkip: {
    id: 'site_pdp.embroidery_confirm_skip',
    defaultMessage: 'Yes, Close',
  },
});

const EmbroideryConfirmationSelectionWrapper = styled.div`
  display: flex;
  color: ${({ theme }) => theme.colors.textDeemphasizedAlternate};

  ${mobile`
    flex-direction: column;
    justify-content: space-between;
  `}
`;

const SectionTitle = styled(Text).attrs({ variant: 'sectionTitle' })`
  ${({ $didSkip }) =>
    $didSkip &&
    css`
      text-align: left;
      margin-bottom: 8px;

      ${mobile`
        text-align: center;
      `}
    `}
  ${mobile`
    margin-bottom: 16px;
  `}
`;

const SkippedText = styled(Text).attrs({ variant: 'bodyMed' })`
  text-align: left;
  color: ${({ theme }) => theme.colors.textDefault};

  ${mobile`
    text-align: center;
  `}
`;

const SkippedTextReason = styled(SkippedText)`
  margin-bottom: 16px;
`;

const SkippedTextQuestion = styled(SkippedText)`
  margin-bottom: 32px;
`;

const EmbroideryAdditionalInfo = styled(Overflow)`
  background: ${({ theme, $didSkip }) =>
    $didSkip
      ? theme.colors.backgroundTableRow
      : theme.colors.backgroundDefault};
  color: white;
  width: 50%;
  padding: 80px 65px;

  ${mobile`
    background: ${({ theme, $didSkip }) =>
      $didSkip ? theme.colors.backgroundTableRow : theme.colors.white};
    color:  ${({ theme }) => theme.colors.textDefault};
    width: 100%;
    height: 45vh;
    padding: 0;

    & > [data-overflow-viewport] {
      // https://css-tricks.com/almanac/properties/s/scrollbar-gutter/
      scrollbar-gutter: stable;
      padding: 48px 24px 15px;
    }
  `}
`;

const AdditionalInfoTitle = styled(Text).attrs({
  variant: 'modalSubheader',
  as: 'h3',
})`
  text-align: left;
  line-height: ${24 / 16};
`;

const FinalSaleTitle = styled(AdditionalInfoTitle)`
  margin-top: 24px;
`;

const AdditionalInfoText = styled(Text).attrs({
  variant: 'embroideryNotification',
})`
  line-height: ${16 / 12};
  letter-spacing: 0.2px;
  text-align: left;
`;

const ConfirmationDetailsPanel = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 50%;
  padding: 50px;

  ${mobile`
    z-index: 5;
    width: 100%;
    padding: 16px 24px 24px;
    background: ${({ theme }) => theme.colors.backgroundLight};
    box-shadow: 0px -4px 5px 0px rgba(0,0,0,0.25);
    transition: box-shadow .5s ease-in-out;
    ${({ canScroll }) => !canScroll && `box-shadow: none`};
  `}
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ConfirmationButton = styled(Button).attrs({ size: 'lg' })`
  width: 100%;
`;

const CancelButton = styled(Button).attrs({ variant: 'secondary', size: 'lg' })`
  width: 100%;
`;

const ErrorMessageWrapper = styled.span`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.error};

  svg {
    margin-right: 12.5px;
  }

  ${mobile`
    margin-bottom: 16px;
  `}
`;

const ErrorMessageText = styled(Text).attrs({
  role: 'alert',
  variant: 'errorText',
})`
  max-width: 80%;
  text-align: left;
  line-height: ${16 / 14};
`;

const DesktopOnly = styled.span`
  ${({ wrapperStyles }) => wrapperStyles};

  ${mobile`
    display: none;
  `}
`;

const MobileOnly = styled.span`
  display: none;

  ${mobile`
    ${({ wrapperStyles }) => wrapperStyles};
  `}
`;

const SHIPPING_NOTE_DEFAULT =
  'Custom embroidered garments take time to stitch, so your order’s shipment may be slightly affected by processing times and order volume.';

const FINAL_SALE_NOTE_DEFAULT =
  'Embroidered items are final sale and cannot be returned or exchanged. Changes cannot be made to your embroidery order once it is processed or shipped. Please ensure that all information is correct before placing your order. The embroidery preview should be used as an approximation of scale and placement. Preview may not represent actual size and is not to scale. All products in an order will wait to ship until embroidery items are completed. This may affect shipping times beyond typical shipping windows. If rush shipping is selected at checkout, your order will ship with rush shipping after your embroidery items are completed. Promotions, offers and discounts cannot be applied toward the cost of embroidery.';

const ERROR_DEFAULT =
  'Uh oh! Something went wrong while adding your design. Please try again or review your selections.';

const SKIP_EXPLANATION_DEFAULT =
  'Because you’ve skipped over our customization options, your garment won’t be embroidered.';

const SKIP_QUESTION_DEFAULT =
  'Are you sure you don’t want to add customization?';

const ErrorMessage = () => (
  <ErrorMessageWrapper>
    <FaExclamationTriangle />
    <ErrorMessageText>
      <FormattedMessage
        id="site_pdp.embroidery_error"
        defaultMessage={ERROR_DEFAULT}
      />
    </ErrorMessageText>
  </ErrorMessageWrapper>
);

export default function EmbroideryConfirmation({
  dialogProps,
  error,
  hasTextCustomizations,
  hasIconCustomizations,
  handleStartOver,
  isLoading,
  onCTAClick,
  onCancel,
  ...rest
}) {
  const { textPersonalizations, iconPersonalizations } =
    useProductDetailEmbroideryContext();

  const prices = useMemo(
    () =>
      getPrices([textPersonalizations, iconPersonalizations].filter(Boolean)),
    [textPersonalizations, iconPersonalizations]
  );

  const [{ canScroll }, setCanScroll] = useState({ canScroll: { down: true } });

  const didSkip = !hasTextCustomizations && !hasIconCustomizations;

  return (
    <EmbroideryConfirmationSelectionWrapper>
      <EmbroideryAdditionalInfo
        $didSkip={didSkip}
        onStateChange={setCanScroll}
        tolerance={1}
      >
        {!didSkip && (
          <Overflow.Content>
            <MobileOnly
              wrapperStyles={`
                display: flex;
                flex-direction: column;
              `}
            >
              <EmbroideryDetailSection
                hasTextCustomizations={hasTextCustomizations}
                hasIconCustomizations={hasIconCustomizations}
                prices={prices}
              />
            </MobileOnly>
            <AdditionalInfoTitle>
              <FormattedMessage
                id="site_pdp.embroidery_shipping_note_PLACEHOLDER"
                defaultMessage="A Note About Shipping"
              />
            </AdditionalInfoTitle>
            <AdditionalInfoText>
              <FormattedMessage
                id="site_pdp.embroidery_shipping_message_PLACEHOLDER"
                defaultMessage={SHIPPING_NOTE_DEFAULT}
              />
            </AdditionalInfoText>
            <AdditionalInfoText />
            <FinalSaleTitle>
              <FormattedMessage
                id="site_pdp.embroidery_final_sale_note_PLACEHOLDER"
                defaultMessage="Final Sale Notice"
              />
            </FinalSaleTitle>
            <AdditionalInfoText>
              <FormattedMessage
                id="site_pdp.embroidery_final_sale_message_PLACEHOLDER"
                defaultMessage={FINAL_SALE_NOTE_DEFAULT}
              />
            </AdditionalInfoText>
          </Overflow.Content>
        )}
      </EmbroideryAdditionalInfo>
      <ConfirmationDetailsPanel canScroll={canScroll.down}>
        <SectionTitle $didSkip={didSkip}>
          <DynamicText
            condition={didSkip}
            defaultText={confirmationMessages.lookGood}
            conditionalText={confirmationMessages.skipped}
          />
        </SectionTitle>

        {didSkip && (
          <>
            <SkippedTextReason>
              <FormattedMessage
                id="site_pdp.embroidery_skipped_reason_PLACEHOLDER"
                defaultMessage={SKIP_EXPLANATION_DEFAULT}
              />
            </SkippedTextReason>
            <SkippedTextQuestion>
              <FormattedMessage
                id="site_pdp.embroidery_skipped_question_PLACEHOLDER"
                defaultMessage={SKIP_QUESTION_DEFAULT}
              />
            </SkippedTextQuestion>
          </>
        )}

        <DesktopOnly
          wrapperStyles={`
          display: flex;
          flex-direction: column;
        `}
        >
          {!didSkip && (
            <EmbroideryDetailSection
              hasTextCustomizations={hasTextCustomizations}
              hasIconCustomizations={hasIconCustomizations}
              prices={prices}
            />
          )}
        </DesktopOnly>

        {error && <ErrorMessage />}

        <ButtonWrapper>
          <ConfirmationButton
            onClick={!didSkip ? onCTAClick : handleStartOver}
            isLoading={isLoading}
            data-autotag="pdp_embroidery_confirm_btn"
          >
            <DynamicText
              condition={didSkip}
              defaultText={confirmationMessages.confirm}
              conditionalText={confirmationMessages.cancelSkip}
            />
          </ConfirmationButton>

          <CancelButton
            onClick={onCancel}
            isLoading={isLoading}
            data-autotag="pdp_embroidery_cancel_btn"
          >
            <DynamicText
              condition={didSkip}
              defaultText={confirmationMessages.cancel}
              conditionalText={confirmationMessages.confirmSkip}
            />
          </CancelButton>
        </ButtonWrapper>
      </ConfirmationDetailsPanel>
    </EmbroideryConfirmationSelectionWrapper>
  );
}

EmbroideryConfirmation.propTypes = {
  dialogProps: PropTypes.object,
  error: PropTypes.bool,
  handleStartOver: PropTypes.func.isRequired,
  hasIconCustomizations: PropTypes.bool.isRequired,
  hasTextCustomizations: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onCTAClick: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
